/**
 * Run a folder of pairs through the batch worker, one at a time.
 *
 * Sequential on purpose. Two pairs in flight would double the peak memory of
 * the thing this app is already bounded by (see `pair-size.ts`), and would race
 * for one worker; a batch that finishes slowly is worth more than one that
 * wedges the tab halfway through.
 *
 * Everything here is orchestration over an injected worker-like object, so the
 * whole sequence — detection, planning, the size refusal, a failing pair — is
 * testable without a real Worker or a real file.
 */

import { assessPairSize } from "../upload/pair-size";
import { planBatch, type BatchPlan, type DetectedFile, type PlannedPair } from "./plan-batch";
import type { BatchArtifact, BatchMessage, BatchRequest } from "../../workers/batch-protocol";
import type { ResolvedSourceProfile } from "../../profiles/resolve";

/** The subset of Worker this runner needs, so tests can inject a fake. */
export type BatchWorkerLike = {
  postMessage: (request: BatchRequest) => void;
  onmessage: ((event: MessageEvent<BatchMessage>) => void) | null;
};

/** A file the person picked, read lazily so the runner holds one pair at most. */
export type BatchInputFile = {
  fileName: string;
  bytes: number;
  text: () => Promise<string>;
};

export type BatchPairOutcome = PlannedPair & {
  pairId: string;
} & (
    | {
        status: "analysed";
        verdict: "Pass" | "Warning" | "Quarantined";
        findingCount: number;
        failures: string[];
        artifacts: BatchArtifact[];
        blocked: Array<{ kind: string; reason: string }>;
      }
    | { status: "refused" | "failed"; reason: string }
  );

export type BatchResult = {
  plan: BatchPlan;
  outcomes: BatchPairOutcome[];
};

export type BatchProgress = {
  /** What the runner is doing now, in a form a person can read. */
  label: string;
  done: number;
  total: number;
};

export async function runBatch(input: {
  worker: BatchWorkerLike;
  files: BatchInputFile[];
  /** Resolved on the main thread; the worker only matches against them. */
  profiles: ResolvedSourceProfile[];
  /** One timestamp for the whole batch, so its artifacts agree with each other. */
  generatedAt: string;
  onProgress?: (progress: BatchProgress) => void;
}): Promise<BatchResult> {
  const { worker, files, profiles, generatedAt } = input;
  const report = (label: string, done: number, total: number) => input.onProgress?.({ label, done, total });

  const byName = new Map(files.map((file) => [file.fileName, file]));
  const profilesById = new Map(profiles.map((profile) => [profile.id, profile]));

  // ---- Detection, one file at a time ----------------------------------------
  const detected: DetectedFile[] = [];
  for (const [index, file] of files.entries()) {
    report(`Identifying ${file.fileName}`, index, files.length);
    let text: string;
    try {
      text = await file.text();
    } catch (error) {
      // One unreadable file is not a reason to abandon the folder; it becomes a
      // named skip like any other file the batch could not place.
      detected.push({
        fileName: file.fileName,
        bytes: file.bytes,
        profileId: null,
        detectionNote: error instanceof Error ? `${error.message}.` : "This file could not be read."
      });
      continue;
    }
    const answer = await request<Extract<BatchMessage, { type: "detected" }>>(
      worker,
      { type: "detect", payload: { fileName: file.fileName, text, profiles } },
      (message) => (message.type === "detected" && message.payload.fileName === file.fileName ? message : null)
    );
    detected.push({
      fileName: file.fileName,
      bytes: file.bytes,
      profileId: answer.payload.profileId,
      ...(answer.payload.note === undefined ? {} : { detectionNote: answer.payload.note })
    });
  }

  const plan = planBatch(detected);
  const outcomes: BatchPairOutcome[] = [];

  // ---- One pair at a time ----------------------------------------------------
  for (const [index, pair] of plan.pairs.entries()) {
    const pairId = `${pair.profileId}:${pair.candidateFileName}`;
    report(`Analysing ${pair.profileId} (${index + 1} of ${plan.pairs.length})`, index, plan.pairs.length);

    const size = assessPairSize(
      byName.get(pair.referenceFileName)?.bytes ?? 0,
      byName.get(pair.candidateFileName)?.bytes ?? 0
    );
    if (size.level === "refuse") {
      // The same ceiling the upload screen enforces. A batch must not be a way
      // around a limit measured on the very thing it does.
      outcomes.push({ ...pair, pairId, status: "refused", reason: size.message });
      continue;
    }

    const profile = profilesById.get(pair.profileId);
    if (profile === undefined) {
      outcomes.push({
        ...pair,
        pairId,
        status: "failed",
        reason: `No resolved profile for ${pair.profileId}; nothing was analysed under a guess.`
      });
      continue;
    }

    const reference = byName.get(pair.referenceFileName);
    const candidate = byName.get(pair.candidateFileName);
    if (reference === undefined || candidate === undefined) {
      outcomes.push({ ...pair, pairId, status: "failed", reason: "A file in this pair is no longer available." });
      continue;
    }

    try {
      const [referenceText, candidateText] = await Promise.all([reference.text(), candidate.text()]);
      const answer = await request<Extract<BatchMessage, { type: "pair-result" } | { type: "pair-error" }>>(
        worker,
        {
          type: "analyze-pair",
          payload: {
            pairId,
            profile,
            referenceFileName: pair.referenceFileName,
            candidateFileName: pair.candidateFileName,
            referenceText,
            candidateText,
            generatedAt
          }
        },
        (message) =>
          (message.type === "pair-result" || message.type === "pair-error") && message.payload.pairId === pairId
            ? message
            : null
      );

      if (answer.type === "pair-error") {
        outcomes.push({ ...pair, pairId, status: "failed", reason: answer.payload.message });
        continue;
      }
      outcomes.push({
        ...pair,
        pairId,
        status: "analysed",
        verdict: answer.payload.verdict,
        findingCount: answer.payload.findingCount,
        failures: answer.payload.failures,
        artifacts: answer.payload.artifacts,
        blocked: answer.payload.blocked
      });
    } catch (error) {
      // A file that cannot be read now is one pair's problem, not the batch's.
      outcomes.push({
        ...pair,
        pairId,
        status: "failed",
        reason: error instanceof Error ? error.message : "Could not read the files for this pair."
      });
    }
  }

  report("Finished", plan.pairs.length, plan.pairs.length);
  return { plan, outcomes };
}

/**
 * Post one request and resolve with the message that answers it.
 *
 * The match function is what correlates answer to question: the worker is one
 * object handling every file and pair, so a reply is claimed by name or pair id,
 * never by arrival order.
 */
function request<TMessage extends BatchMessage>(
  worker: BatchWorkerLike,
  message: BatchRequest,
  match: (message: BatchMessage) => TMessage | null
): Promise<TMessage> {
  return new Promise<TMessage>((resolve) => {
    const previous = worker.onmessage;
    worker.onmessage = (event: MessageEvent<BatchMessage>) => {
      const matched = match(event.data);
      if (matched === null) return;
      worker.onmessage = previous;
      resolve(matched);
    };
    worker.postMessage(message);
  });
}
